import { useLocation } from "react-router-dom";

export type TestCreationQuery = {
  test_id: string;
  is_clone?: boolean;
  old_test_id?: string;
};

export type ConfigureStudentsQuery = {
  test_id: string;
  batch_id?: string;
  course_id?: string;
  track_id?: string;
  slot_id?: string;
};

export type ReplaceQuestionQuery = {
  test_id: string;
  slot_id: string;
  question_id: string;
  subject_name?: string;
  topic_name?: string;
  sub_topic_name?: string;
};

export type TestRoutes = {
  create: string;
  configure: string;
  reports: string;
  reportDetail: (testName: string, testId: string) => string;
  testCreation: (testName: string, query: TestCreationQuery) => string;
  configureDetail: (
    testName: string,
    testId: string,
    options?: { reopen_slot?: string | number }
  ) => string;
  configureStudents: (
    testName: string,
    query: ConfigureStudentsQuery
  ) => string;
  replaceQuestion: (testName: string, query: ReplaceQuestionQuery) => string;
};

export const encodeTestNameForPath = (testName: string): string =>
  encodeURIComponent(testName.trim());

export const decodeTestNameFromPath = (segment: string | undefined): string =>
  segment ? decodeURIComponent(segment) : "";

const buildSearchQuery = (params: Record<string, string | undefined>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

const makeTestRoutes = (prefix: "/admin" | "/trainer"): TestRoutes => ({
  create: `${prefix}/test/create`,
  configure: `${prefix}/test/configure`,
  reports: `${prefix}/test/reports`,
  reportDetail: (testName, testId) =>
    `${prefix}/test/reports/${encodeTestNameForPath(testName)}?test_id=${encodeURIComponent(testId)}`,
  testCreation: (testName, query) =>
    `${prefix}/test-creation/${encodeTestNameForPath(testName)}${buildSearchQuery({
      test_id: query.test_id,
      old_test_id: query.old_test_id,
      is_clone: query.is_clone ? "true" : undefined,
    })}`,
  configureDetail: (testName, testId, options) =>
    `${prefix}/test/configure/${encodeTestNameForPath(testName)}${buildSearchQuery({
      test_id: testId,
      reopen_slot:
        options?.reopen_slot != null ? String(options.reopen_slot) : undefined,
    })}`,
  configureStudents: (testName, query) =>
    `${prefix}/test/configure/${encodeTestNameForPath(testName)}/students${buildSearchQuery({
      test_id: query.test_id,
      batch_id: query.batch_id,
      course_id: query.course_id,
      track_id: query.track_id,
      slot_id: query.slot_id,
    })}`,
  replaceQuestion: (testName, query) =>
    `${prefix}/test/configure/${encodeTestNameForPath(testName)}/replace-question${buildSearchQuery(
      {
        test_id: query.test_id,
        slot_id: query.slot_id,
        question_id: query.question_id,
        subject_name: query.subject_name,
        topic_name: query.topic_name,
        sub_topic_name: query.sub_topic_name,
      }
    )}`,
});

const ADMIN_TEST_ROUTES = makeTestRoutes("/admin");
const TRAINER_TEST_ROUTES = makeTestRoutes("/trainer");

export const useTestRoutes = (): TestRoutes => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/trainer/")) return TRAINER_TEST_ROUTES;
  return ADMIN_TEST_ROUTES;
};

export const isTestCreationPath = (pathname: string): boolean =>
  /\/test-creation\/[^/]+/.test(pathname);

export const isTestCreatePath = (pathname: string): boolean =>
  pathname.endsWith("/test/create");

export const isTestConfigurePath = (pathname: string): boolean =>
  /\/test\/configure(\/|$)/.test(pathname) ||
  pathname.includes("/test/assign");

export const isTestReportsPath = (pathname: string): boolean =>
  /\/test\/reports(\/|$)/.test(pathname) ||
  /\/test\/report(\/|$)/.test(pathname);
